// =============================================================
// VARIANCE — The Executive Command Center
// File      : DealPipeline.tsx
// Location  : /home/Applications/variance/web/src/pages/DealPipeline.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Kanban board for managing active sales deals.
// =============================================================

import { useState, useEffect } from 'react';
import { Plus, ArrowLeft, Building2, IndianRupee } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STAGES = [
  'Feasibility',
  'Estimation',
  'Under Review',
  'Contracting',
  'Pending',
  'Closed Won',
  'Closed Lost'
];

interface Deal {
  id: string;
  customer_name: string;
  project_name: string;
  deal_value: number | null;
  probability: number;
  status: string;
}

export function DealPipeline() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null);

  const fetchDeals = () => {
    setIsLoading(true);
    fetch('/api/sales/deals')
      .then(res => res.json())
      .then(data => { if (!data.error) setDeals(data); setIsLoading(false); })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchDeals();
  }, []);

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedDealId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    if (!draggedDealId) return;

    setDeals(prev => prev.map(d => d.id === draggedDealId ? { ...d, status: newStatus } : d));

    try {
      await fetch(`/api/sales/deals/${draggedDealId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
    } catch (error) {
      console.error('Failed to update status', error);
      fetchDeals();
    }
    setDraggedDealId(null);
  };

  return (
    <div className="h-full flex flex-col space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/sales')} className="p-2 rounded-full hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ color: 'var(--text-secondary)' }}>
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Deal Pipeline</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Manage negotiations and project handoffs.</p>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white hover:opacity-90 transition-all shadow-[0_0_15px_var(--brand-accent)]" style={{ backgroundColor: 'var(--brand-accent)' }}>
          <Plus size={18} /> Add Deal
        </button>
      </div>

      <div className="flex-1 flex gap-4 overflow-x-auto pb-4">
        {isLoading ? (
          <div className="w-full flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>Loading deals...</div>
        ) : (
          STAGES.map(stage => {
            const stageDeals = deals.filter(d => d.status === stage);
            return (
              <div key={stage} className="flex-shrink-0 w-72 flex flex-col rounded-xl" style={{ backgroundColor: 'var(--bg-panel-alt)', border: '1px solid var(--border-subtle)' }} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, stage)}>
                <div className="p-3 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-subtle)' }}>
                  <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{stage}</h3>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--text-secondary)' }}>{stageDeals.length}</span>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[150px]">
                  {stageDeals.map(deal => (
                    <div key={deal.id} draggable onDragStart={(e) => handleDragStart(e, deal.id)} className="p-3 rounded-lg cursor-grab active:cursor-grabbing hover:border-[var(--brand-accent)] transition-colors" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                      <div className="font-medium text-sm mb-1" style={{ color: 'var(--text-primary)' }}>{deal.customer_name || 'Unknown Customer'}</div>
                      <div className="flex items-center gap-1 text-xs mb-2" style={{ color: 'var(--brand-accent)' }}><Building2 size={12} /> {deal.project_name}</div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                        <div className="flex items-center gap-1 text-xs font-mono font-bold" style={{ color: 'var(--text-primary)' }}><IndianRupee size={12} /> {(deal.deal_value || 0).toLocaleString('en-IN')}</div>
                        <div className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[var(--bg-panel-alt)]" style={{ color: 'var(--text-secondary)' }}>{deal.probability}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
